import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { useCollaboratorsPublicQuery } from '@/features/Collaborators/Data';
import { lerCsvGuardioes, NOTA_MINIMA_GUARDIAO, prepararImportacao } from './guardioesCsv';
import type { ResultadoImportacao, SituacaoImportacao } from './guardioesCsv';
import { guardioesService } from './guardioesService';
import type { Guardiao } from './guardioesService';
import { GUARDIOES_QUERY_KEY } from './useGuardioes';

const ROTULO: Record<SituacaoImportacao, { texto: string; classe: string }> = {
  novo: { texto: 'Novo guardião', classe: 'text-green-700' },
  ja_estava: { texto: 'Já era guardião', classe: 'text-raro-gray-500' },
  abaixo: { texto: `Abaixo de ${NOTA_MINIMA_GUARDIAO}%`, classe: 'text-amber-700' },
  sem_cadastro: { texto: 'Sem cadastro no Digital — ignorado', classe: 'text-rose-700' },
  nome_repetido: { texto: 'Sem e-mail e nome repetido — ignorado', classe: 'text-rose-700' },
};

/** SI: importa o CSV do Console do Admin do Guardiões (até a sincronização automática existir). */
export function ImportarGuardioes({ jaGuardioes }: { jaGuardioes: Guardiao[] }) {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: colaboradores = [] } = useCollaboratorsPublicQuery();
  const [resultado, setResultado] = useState<ResultadoImportacao[] | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const ultima = useQuery({ queryKey: [...GUARDIOES_QUERY_KEY, 'ultima-importacao'], queryFn: () => guardioesService.ultimaImportacao() });
  const ultimaEm = ultima.data?.em?.toDate?.();

  const contagem = useMemo(() => {
    const c: Record<SituacaoImportacao, number> = { novo: 0, ja_estava: 0, abaixo: 0, sem_cadastro: 0, nome_repetido: 0 };
    (resultado ?? []).forEach((r) => (c[r.situacao] += 1));
    return c;
  }, [resultado]);

  const ler = async (arquivo: File) => {
    const linhas = lerCsvGuardioes(await arquivo.text());
    if (linhas.length === 0) {
      toast.error('Não encontrei nenhuma linha nesse arquivo. Confira se é o CSV exportado do Guardiões.');
      return;
    }
    setNomeArquivo(arquivo.name);
    setResultado(
      prepararImportacao(
        linhas,
        colaboradores.map((c) => ({ id: c.id, name: c.name, email: c.email ?? '' })).filter((c) => c.email),
        new Set(jaGuardioes.map((g) => g.email.toLowerCase()))
      )
    );
  };

  const confirmar = async () => {
    if (!resultado || !user) return;
    setSalvando(true);
    try {
      const total = await guardioesService.importar(
        resultado,
        { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '' },
        nomeArquivo
      );
      toast.success(total === 0 ? 'Nenhum guardião novo nesse arquivo.' : `${total} novo(s) guardião(ões) adicionado(s).`);
      setResultado(null);
      setNomeArquivo('');
      queryClient.invalidateQueries({ queryKey: GUARDIOES_QUERY_KEY });
    } catch (error) {
      toast.error(`Erro ao importar: ${(error as { message?: string })?.message ?? 'erro desconhecido'}`);
    } finally {
      setSalvando(false);
    }
  };

  const ordem: SituacaoImportacao[] = ['novo', 'sem_cadastro', 'nome_repetido', 'abaixo', 'ja_estava'];

  return (
    <section className="space-y-3 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm">
      <div>
        <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">Atualizar a lista de Guardiões</h2>
        <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-sm text-raro-gray-600">
          <li>
            No Guardiões, abra o <b>Console do Admin</b> → <b>Conclusões &amp; Certificações</b> → <b>Exportar CSV</b>.
          </li>
          <li>Envie o arquivo aqui. Quem tirou {NOTA_MINIMA_GUARDIAO}% ou mais vira Guardião (liga pelo e-mail; sem e-mail, pelo nome).</li>
        </ol>
        <p className="mt-1 text-xs text-raro-gray-500">Só você (SI) vê esta parte. Quem já é Guardião continua — o selo vale pra sempre.</p>
      </div>

      <label className="flex cursor-pointer justify-center rounded-lg border border-dashed border-raro-gray-300 px-3 py-4 text-sm text-raro-gray-500 hover:border-primary-300 hover:bg-primary-50/40">
        {nomeArquivo ? (
          <span>
            📎 {nomeArquivo} <span className="ml-1 font-semibold text-green-700">✓ lido</span>
          </span>
        ) : (
          'Escolher o CSV do Guardiões'
        )}
        <input
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(event) => {
            const arquivo = event.target.files?.[0];
            if (arquivo) void ler(arquivo);
            event.target.value = '';
          }}
        />
      </label>
      <p className="text-xs text-raro-gray-500">
        {ultima.isLoading
          ? 'Carregando a última importação...'
          : ultimaEm
            ? `Última importação: ${ultimaEm.toLocaleDateString('pt-BR')} às ${ultimaEm.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}${
                ultima.data?.por?.name ? ` · por ${ultima.data.por.name}` : ''
              } · ${ultima.data?.novos ?? 0} novo(s)${ultima.data?.arquivo ? ` · ${ultima.data.arquivo}` : ''}`
            : 'Nenhuma importação feita ainda.'}
      </p>

      {resultado && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-green-50 p-3">
              <p className="text-xs text-green-700">Novos guardiões</p>
              <p className="text-xl font-bold text-green-800">{contagem.novo}</p>
            </div>
            <div className="rounded-xl bg-raro-gray-50 p-3">
              <p className="text-xs text-raro-gray-500">Já eram</p>
              <p className="text-xl font-bold">{contagem.ja_estava}</p>
            </div>
            <div className="rounded-xl bg-amber-50 p-3">
              <p className="text-xs text-amber-700">Abaixo de {NOTA_MINIMA_GUARDIAO}%</p>
              <p className="text-xl font-bold text-amber-800">{contagem.abaixo}</p>
            </div>
            <div className="rounded-xl bg-rose-50 p-3">
              <p className="text-xs text-rose-700">Não ligados</p>
              <p className="text-xl font-bold text-rose-800">{contagem.sem_cadastro + contagem.nome_repetido}</p>
            </div>
          </div>
          <ul className="max-h-72 divide-y divide-raro-gray-100 overflow-y-auto rounded-xl border border-raro-gray-100 text-sm">
            {[...resultado]
              .sort((a, b) => ordem.indexOf(a.situacao) - ordem.indexOf(b.situacao))
              .map((r, i) => (
                <li key={`${r.email ?? r.nome}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2">
                  <span className="min-w-0 truncate">
                    {r.nome} {r.email && <span className="text-xs text-raro-gray-400">· {r.email}</span>}
                  </span>
                  <span className={`shrink-0 text-xs font-semibold ${ROTULO[r.situacao].classe}`}>{ROTULO[r.situacao].texto}</span>
                </li>
              ))}
          </ul>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="btn-secondary"
              disabled={salvando}
              onClick={() => {
                setResultado(null);
                setNomeArquivo('');
              }}
            >
              Cancelar
            </button>
            <button type="button" className="btn-primary" disabled={salvando} onClick={confirmar}>
              {salvando ? 'Importando...' : contagem.novo === 0 ? 'Registrar importação (nenhum novo)' : `Confirmar importação (${contagem.novo})`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
